import { GOOGLE } from "../../../utils/constants";
import {
  ADS_CAMPAIGNS_OBJECTIVES_SUPPORTED_BY_PLATFORM,
  filterMediaByPlatform,
} from "../../../utils/platforms";
import { formatMediaUrl } from "../../../services/adsCampaigns";
import { PENDING_GALLERY_CREATIVES_KEY } from "../../../hooks/galleryCreativeMedia";

const CREATIVE_TYPES = {
  CAROUSEL: "carousel",
  IMAGE: "image",
  VIDEO: "video",
};

const parseCreativeUrls = (urls) =>
  urls
    .map((url) => (typeof url === "string" ? url.trim() : url?.url?.trim()))
    .filter(Boolean);

const creativeToFormattedMedia = (creative, formatMediaUrlFn) => {
  if (
    creative?.type === CREATIVE_TYPES.CAROUSEL &&
    Array.isArray(creative?.urls)
  ) {
    const urls = parseCreativeUrls(creative.urls);
    if (urls.length === 0) return null;
    return formatMediaUrlFn({
      url: urls[0],
      isCarousel: true,
      carouselImages: urls.map((url) => ({ url })),
      from: "upload",
    });
  }

  if (
    (creative?.type === CREATIVE_TYPES.IMAGE ||
      creative?.type === CREATIVE_TYPES.VIDEO) &&
    creative?.url
  ) {
    return formatMediaUrlFn({
      url: creative.url.trim(),
      from: "upload",
    });
  }

  return null;
};

export const parseInitialCreatives = (
  initialCreatives,
  formatMediaUrlFn = formatMediaUrl
) => {
  if (!initialCreatives) {
    return [];
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(initialCreatives));
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((creative) => creativeToFormattedMedia(creative, formatMediaUrlFn))
      .filter(Boolean);
  } catch {
    const urls = String(initialCreatives).split(",").filter(Boolean);
    return urls.map((url) =>
      formatMediaUrlFn({ url: url.trim(), from: "upload" })
    );
  }
};

export const buildFirstPlatformInitialData = async ({
  brandInformation,
  initialCreatives,
  platform,
  formatMediaUrlFn = formatMediaUrl,
  filterMediaByPlatformFn = filterMediaByPlatform,
} = {}) => {
  const initialData = {
    destinationURL: brandInformation?.websiteUrl || "",
  };

  if (!initialCreatives) {
    return initialData;
  }

  const mediaUrlsFormatted = parseInitialCreatives(
    initialCreatives,
    formatMediaUrlFn
  );
  const filteredMediaUrls = await filterMediaByPlatformFn(
    mediaUrlsFormatted,
    platform
  );
  if (filteredMediaUrls.length > 0) {
    initialData[PENDING_GALLERY_CREATIVES_KEY] = filteredMediaUrls;
  }

  return initialData;
};

export const buildAdditionalPlatformInitialData = ({
  getValues,
  platform,
  formatMediaUrlFn = formatMediaUrl,
} = {}) => {
  const platforms = getValues?.("platforms") || [];
  let objective = "";
  let destinationURL = "";
  const mediaUrls = [];
  const mediaData = [];

  platforms.forEach((platformIteration) => {
    if (platformIteration === platform) return;

    const platformData = getValues(`platformsData.${platformIteration}`) || {};
    if (!objective) {
      const objectivePlatform = platformData?.objective;
      const foundedObjective = ADS_CAMPAIGNS_OBJECTIVES_SUPPORTED_BY_PLATFORM[
        platformIteration
      ]?.find((option) => option.value === objectivePlatform);
      if (foundedObjective) {
        objective = foundedObjective.value;
      }
    }

    if (platformIteration === GOOGLE) {
      const imagesUrls = platformData?.imagesUrls || [];
      imagesUrls.forEach((imageUrl) => {
        if (mediaUrls.includes(imageUrl)) return;
        mediaUrls.push(imageUrl);
        mediaData.push({ from: "upload" });
      });
    }
  });

  const adsGroups = getValues("adsGroups") || [];
  adsGroups.forEach((adGroup) => {
    if (adGroup.platform === platform) return;

    (adGroup.ads || []).forEach((ad) => {
      const destinationUrls = ad.destinationUrls || [];
      if (!destinationURL && destinationUrls.length > 0) {
        destinationURL = destinationUrls?.[0]?.value;
      }

      (ad.mediaUrls || []).forEach((mediaUrl, index) => {
        if (mediaUrl === null || mediaUrls.includes(mediaUrl)) return;
        mediaUrls.push(mediaUrl);
        mediaData.push(ad.mediaData?.[index] || { from: "upload" });
      });
    });
  });

  const assetsGroups = getValues("assetsGroups") || [];
  assetsGroups.forEach((assetsGroup) => {
    if (assetsGroup.platform === platform) return;

    const mediaUrlsAssetsGroup = [
      ...(assetsGroup.imageUrls || []),
      ...(assetsGroup.mediaUrls || []),
    ];

    mediaUrlsAssetsGroup.forEach((mediaUrl) => {
      const url = typeof mediaUrl === "string" ? mediaUrl : mediaUrl?.url;
      if (!url || mediaUrls.includes(url)) return;
      mediaUrls.push(url);
      mediaData.push({ from: "upload" });
    });

    if (!destinationURL && assetsGroup?.destinationUrl) {
      destinationURL = assetsGroup.destinationUrl;
    }
  });

  const pendingGalleryCreatives = mediaUrls.map((mediaUrl, index) =>
    formatMediaUrlFn({
      url: mediaUrl,
      ...(mediaData[index] || {}),
    })
  );

  return {
    objective,
    destinationURL,
    ...(pendingGalleryCreatives.length > 0
      ? { [PENDING_GALLERY_CREATIVES_KEY]: pendingGalleryCreatives }
      : {}),
  };
};

export const getInitialPlatformSetupData = async ({
  platform,
  isFirstPlatform,
  brandInformation,
  initialCreatives,
  getValues,
} = {}) => {
  if (isFirstPlatform) {
    return buildFirstPlatformInitialData({
      brandInformation,
      initialCreatives,
      platform,
    });
  }

  return buildAdditionalPlatformInitialData({
    getValues,
    platform,
  });
};
